import styles from "./FirmsPage.module.css";
import {
  Alert,
  Box,
  Button,
  PageHeader,
  Spinner,
} from "@justworkshr/milo-core";
import {
  InfoBox,
  LegacyTabList,
  PendingFirmTable,
  ActiveFirmTable,
  RemoveFirmModal,
  ThirdPartyAdminTable,
} from "./components";
import {
  FirmStatus,
  useGetAllClientInvitesQuery,
  useGetAllFirmInvitesQuery,
} from "types/generated/operations";
import { useFirmsPageQueries, useConsumeSuccessMessage } from "./hooks";
import { canManageFirms } from "pages/advisor/common/utils";
import { useParams } from "react-router-dom";
import { RemovePendingFirmModal } from "./components/RemovePendingFirmModal";

const { firmsPage, firmsPageHeader, section, alert } = styles;

export type FirmTableMember = {
  uuid: string;
  firstName: string;
  lastName: string;
  emailWork: string;
  deskTitle: string;
  firmName?: string | null;
  phoneNumber: string | null;
};

const FirmsPage = () => {
  const { data, loading, error } = useFirmsPageQueries();
  const { data: allFirmsInviteData } = useGetAllFirmInvitesQuery();
  const { data: allClientInvitesData, refetch } = useGetAllClientInvitesQuery();
  const { modalAction } = useParams();

  const firm = data?.firm;
  const thirdPartyAdmins =
    data?.authenticatedMember?.company?.thirdPartyAdmins ?? [];
  const allFirmInvites = allFirmsInviteData?.allFirmInvites ?? [];
  const pendingClientInvite = allClientInvitesData?.allClientInvites?.find(
    (invite) => invite.status === "PENDING"
  );

  const successMessage = useConsumeSuccessMessage();
  const showFirmInviteMessage = Boolean(
    allFirmsInviteData?.allFirmInvites.length && !firm
  );
  const canManage = canManageFirms(data?.authenticatedMember?.permissions);

  const latestFirmInvite = allFirmInvites.length
    ? allFirmInvites?.reduce((latest, current) => {
        return new Date(latest.expiresAt) > new Date(current.expiresAt)
          ? latest
          : current;
      })
    : undefined;

  const firmInvitationUrl = `/cf/firm-invitation/${latestFirmInvite?.uuid}`;
  return (
    <>
      {successMessage && (
        <Alert color="additive" visible dismissible>
          {successMessage}
        </Alert>
      )}
      {showFirmInviteMessage && (
        <Alert color="new" visible className={alert}>
          <p>
            <b>
              {latestFirmInvite?.invitingFirmUser?.firstName}{" "}
              {latestFirmInvite?.invitingFirmUser?.lastName}
            </b>{" "}
            from <b>{latestFirmInvite?.firmName}</b> is requesting to be added
            to <b>{data?.authenticatedMember?.company.name}</b>'s account.
          </p>
          <Button
            as="a"
            href={firmInvitationUrl}
            size="sm"
            variant="ghost"
            color="neutral"
          >
            View Request
          </Button>
        </Alert>
      )}

      <div className={`${firmsPage} milo--grid`}>
        <PageHeader className={firmsPageHeader} title="Team management" />
        {loading ? (
          <Spinner />
        ) : Boolean(error) ? (
          <Alert visible={Boolean(error)} color="destructive">
            We were unable to load third-party members. Please try again.
          </Alert>
        ) : !canManage ? (
          <Box backgroundColor="warningSubtle" padding="2xl">
            <>
              You don't have permission to do that. Please make sure you're
              logged into the correct account. If you think you're seeing this
              message in error please feel free to{" "}
              <a href="https://justworks.desk.com/">contact us</a>.
            </>
          </Box>
        ) : (
          <>
            <LegacyTabList />
            <div className={section}>
              <InfoBox />
            </div>
            {firm && firm.status == FirmStatus.Active && (
              <div key={firm.uuid} className={section}>
                <ActiveFirmTable firm={firm} />
              </div>
            )}
            {pendingClientInvite && (
              <div key={pendingClientInvite.id} className={section}>
                <PendingFirmTable firm={pendingClientInvite} />
              </div>
            )}
            {thirdPartyAdmins.length > 0 && (
              <div className={section}>
                <ThirdPartyAdminTable members={thirdPartyAdmins} />
              </div>
            )}
          </>
        )}
      </div>
      {firm && modalAction === "remove-firm" && (
        <RemoveFirmModal firm={firm} companyUuid={data.companyUuid} />
      )}
      {pendingClientInvite && modalAction === "remove-pending-firm" && (
        <RemovePendingFirmModal
          firmInvite={pendingClientInvite}
          refetchData={refetch}
        />
      )}
    </>
  );
};

export default FirmsPage;
